import { Reveal, Overline } from "./Reveal";
import { Building2, Star, ShoppingBag, BusFront, ArrowUpRight, Phone, GraduationCap } from "lucide-react";
import { PROJECTS, COPOU_AGENT } from "../data/content";

export const Living = ({ t, lang }) => (
  <section id="living" data-testid="living-section" className="py-24 md:py-36 bg-copou-bg">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal className="max-w-2xl mb-14">
        <Overline>{t.living.overline}</Overline>
        <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-4 text-copou-ink">{t.living.title}</h2>
        <p className="mt-5 text-copou-muted leading-relaxed">{t.living.intro}</p>
      </Reveal>

      {/* Objectives */}
      <Reveal className="mb-16 bg-copou-surface border border-copou-line p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
        <div className="w-12 h-12 shrink-0 flex items-center justify-center border border-copou-primary text-copou-primary">
          <GraduationCap size={22} strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-serif text-2xl text-copou-ink mb-3">{t.living.objTitle}</h3>
          <p className="text-copou-muted leading-relaxed">{t.living.objText}</p>
        </div>
      </Reveal>

      {/* Projects */}
      <Reveal className="mb-8">
        <h3 className="font-serif text-3xl font-light text-copou-ink">{t.living.projectsTitle}</h3>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6 mb-20">
        {PROJECTS.map((p, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08}>
            <div
              data-testid={`project-card-${i}`}
              className={`group h-full border overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                p.featured ? "bg-copou-ink text-white border-copou-ink" : "bg-copou-surface border-copou-line"
              }`}
            >
              {p.img && (
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {p.featured && (
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-copou-bg/95 text-copou-primary text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5">
                      <Star size={12} fill="currentColor" /> UNU Imobiliare
                    </span>
                  )}
                </div>
              )}
              <div className="p-8">
                <div className="w-11 h-11 flex items-center justify-center border border-copou-primary text-copou-primary">
                  <Building2 size={20} strokeWidth={1.5} />
                </div>
                <h4 className={`font-serif text-2xl mt-5 ${p.featured ? "text-white" : "text-copou-ink"}`}>{p.name}</h4>
                <p className={`text-xs uppercase tracking-wider mt-1 ${p.featured ? "text-white/60" : "text-copou-muted"}`}>{p.dev[lang]}</p>
                <p className={`mt-4 leading-relaxed ${p.featured ? "text-white/80" : "text-copou-muted"}`}>{p.desc[lang]}</p>
                <ul className={`mt-5 space-y-1.5 text-sm ${p.featured ? "text-white/80" : "text-copou-ink"}`}>
                  {p.facts[lang].map((f, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-copou-primary rounded-full shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`project-link-${i}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-copou-primary group-hover:gap-3 transition-all"
                  >
                    {t.living.projectCta} <ArrowUpRight size={16} />
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Shops + Transport */}
      <div className="grid md:grid-cols-2 gap-6">
        <Reveal>
          <div className="h-full bg-copou-surface border border-copou-line p-8 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <ShoppingBag size={22} className="text-copou-primary" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl text-copou-ink">{t.living.shopsTitle}</h3>
            </div>
            <ul className="space-y-3">
              {t.living.shops.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-copou-muted">
                  <span className="w-1.5 h-1.5 bg-copou-primary rounded-full mt-2.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="h-full bg-copou-surface border border-copou-line p-8 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <BusFront size={22} className="text-copou-primary" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl text-copou-ink">{t.living.transportTitle}</h3>
            </div>
            <ul className="space-y-3">
              {t.living.transport.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-copou-muted">
                  <span className="w-1.5 h-1.5 bg-copou-primary rounded-full mt-2.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      {/* Agent CTA */}
      <Reveal delay={0.1} className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-5 bg-copou-primary text-white p-8 md:p-10">
        <p className="font-serif text-2xl md:text-3xl font-light">{t.living.ctaText}</p>
        <a
          href={`tel:${COPOU_AGENT.phoneTel}`}
          data-testid="living-agent-cta"
          className="inline-flex items-center gap-2 bg-white text-copou-primary px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-copou-bg transition-colors whitespace-nowrap"
        >
          <Phone size={16} /> {t.living.ctaBtn}
        </a>
      </Reveal>
    </div>
  </section>
);
